/**
 * @fileoverview Responsive Navbar with Explorer dropdown and authentication handling.
 * Supports mobile menu, route awareness, and login/logout logic.
 * @author ISCRI
 */

import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@router";
import { useAuth } from "@hooks/useAuth";
import logoDark from "@assets/images/iscri-logo-dark.png";
import "./Navbar.css";

/**
 * Main Navbar component.
 * - Handles route awareness
 * - Dropdown for Data Explorer
 * - Mobile responsiveness
 * - Authenticated Logout/Login
 */
const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isExplorerDropdownOpen, setIsExplorerDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  /**
   * Toggle mobile menu visibility
   */
  const toggleMobileMenu = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  /**
   * Toggle explorer dropdown visibility
   */
  const toggleExplorerDropdown = (e) => {
    e.preventDefault();
    setIsExplorerDropdownOpen(!isExplorerDropdownOpen);
  };

  /**
   * Close all dropdowns and mobile menu
   */
  const closeAllDropdowns = () => {
    setIsExplorerDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  /**
   * Logout the user, redirect to home, and close dropdowns
   */
  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
      navigate(ROUTES.HOME);
      closeAllDropdowns();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  /**
   * Add/remove 'scrolled' class to navbar when page is scrolled past threshold
   */
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.querySelector(".navbar");
      if (navbar) {
        if (window.scrollY > 50) {
          navbar.classList.add("scrolled");
          setIsScrolled(true);
        } else {
          navbar.classList.remove("scrolled");
          setIsScrolled(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Close the Explorer dropdown when clicking outside of it
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".nav-dropdown")) {
        setIsExplorerDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  /**
   * Close dropdowns when route changes
   */
  useEffect(() => {
    closeAllDropdowns();
  }, [location.pathname]);

  /**
   * Check if current route is active
   * @param {string} path - Route path to check
   * @returns {boolean} - Whether the route is active
   */
  const isActiveRoute = (path) => {
    const currentPath = location.pathname;

    if (path === ROUTES.HOME) {
      return currentPath === "/" || currentPath === "/iscri.html";
    }

    return currentPath === path;
  };

  /**
   * Check if the current route is part of the Data Explorer section
   * Used to highlight the Explorer dropdown
   */
  const isExplorerRouteActive = () => {
    return [ROUTES.SIMPLE, ROUTES.ADVANCED, ROUTES.DOWNLOADS].includes(
      location.pathname
    );
  };

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
      <div className="nav-container">
        <Link to={ROUTES.HOME} className="nav-logo" onClick={closeAllDropdowns}>
          <img src={logoDark} alt="ISCRI Logo" />
        </Link>

        {/* Menu Desktop */}
        <ul className="nav-menu">
          <li className="nav-item">
            <Link
              to={ROUTES.HOME}
              className={`nav-link ${
                isActiveRoute(ROUTES.HOME) ? "active" : ""
              }`}
            >
              Home
            </Link>
          </li>
          <li className="nav-item">
            <Link
              to={ROUTES.ABOUT}
              className={`nav-link ${
                isActiveRoute(ROUTES.ABOUT) ? "active" : ""
              }`}
            >
              About
            </Link>
          </li>
          <li className="nav-item">
            <Link
              to={ROUTES.METHODOLOGY}
              className={`nav-link ${
                isActiveRoute(ROUTES.METHODOLOGY) ? "active" : ""
              }`}
            >
              Methodology
            </Link>
          </li>

          {/* Explorer Dropdown */}
          <li className="nav-item nav-dropdown">
            <button
              className={`nav-link dropdown-toggle ${
                isExplorerRouteActive() ? "active" : ""
              }`}
              onClick={toggleExplorerDropdown}
              aria-expanded={isExplorerDropdownOpen}
              aria-haspopup="true"
            >
              Data Explorer
              <svg
                className={`dropdown-arrow ${
                  isExplorerDropdownOpen ? "open" : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>

            <ul
              className={`dropdown-menu ${
                isExplorerDropdownOpen ? "show" : ""
              }`}
            >
              <li>
                <Link
                  to={ROUTES.SIMPLE}
                  className={`dropdown-item ${
                    isActiveRoute(ROUTES.SIMPLE) ? "active" : ""
                  }`}
                  onClick={closeAllDropdowns}
                >
                  Simple Explorer
                </Link>
              </li>
              {isAuthenticated && (
                <>
                  <li>
                    <Link
                      to={ROUTES.ADVANCED}
                      className={`dropdown-item ${
                        isActiveRoute(ROUTES.ADVANCED) ? "active" : ""
                      }`}
                      onClick={closeAllDropdowns}
                    >
                      Advanced Explorer
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={ROUTES.DOWNLOADS}
                      className={`dropdown-item ${
                        isActiveRoute(ROUTES.DOWNLOADS) ? "active" : ""
                      }`}
                      onClick={closeAllDropdowns}
                    >
                      Data Download
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </li>

          {/* Login/Logout */}
          <li className="nav-item">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="nav-link logout-btn"
                title="Sign out"
              >
                Logout
              </button>
            ) : (
              <Link to={ROUTES.LOGIN} className="nav-link login-btn">
                Login
              </Link>
            )}
          </li>
        </ul>

        {/* Menu Hamburger */}
        <div
          className={`nav-toggle ${isMobileMenuOpen ? "active" : ""}`}
          id="nav-toggle"
          onClick={toggleMobileMenu}
          role="button"
          tabIndex={0}
          aria-label="Toggle mobile menu"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              toggleMobileMenu(e);
            }
          }}
        >
          <div className="hamburger"></div>
          <div className="hamburger"></div>
          <div className="hamburger"></div>
        </div>
      </div>

      {/* Menu Mobile */}
      <ul
        className={`nav-menu-mobile ${isMobileMenuOpen ? "active" : ""}`}
        id="nav-menu-mobile"
      >
        <li className="nav-item">
          <Link
            to={ROUTES.HOME}
            className={`nav-link ${isActiveRoute(ROUTES.HOME) ? "active" : ""}`}
            onClick={closeAllDropdowns}
          >
            Home
          </Link>
        </li>
        <li className="nav-item">
          <Link
            to={ROUTES.ABOUT}
            className={`nav-link ${
              isActiveRoute(ROUTES.ABOUT) ? "active" : ""
            }`}
            onClick={closeAllDropdowns}
          >
            About
          </Link>
        </li>
        <li className="nav-item">
          <Link
            to={ROUTES.METHODOLOGY}
            className={`nav-link ${
              isActiveRoute(ROUTES.METHODOLOGY) ? "active" : ""
            }`}
            onClick={closeAllDropdowns}
          >
            Methodology
          </Link>
        </li>

        {/* Explorer Section Mobile */}
        <li className="nav-item mobile-section">
          <span className="mobile-section-title">Data Explorer</span>
        </li>
        <li className="nav-item mobile-sub-item">
          <Link
            to={ROUTES.SIMPLE}
            className={`nav-link ${
              isActiveRoute(ROUTES.SIMPLE) ? "active" : ""
            }`}
            onClick={closeAllDropdowns}
          >
            Simple Explorer
          </Link>
        </li>
        {isAuthenticated && (
          <>
            <li className="nav-item mobile-sub-item">
              <Link
                to={ROUTES.ADVANCED}
                className={`nav-link ${
                  isActiveRoute(ROUTES.ADVANCED) ? "active" : ""
                }`}
                onClick={closeAllDropdowns}
              >
                Advanced Explorer
              </Link>
            </li>
            <li className="nav-item mobile-sub-item">
              <Link
                to={ROUTES.DOWNLOADS}
                className={`nav-link ${
                  isActiveRoute(ROUTES.DOWNLOADS) ? "active" : ""
                }`}
                onClick={closeAllDropdowns}
              >
                Data Download
              </Link>
            </li>
          </>
        )}

        {/* Login/Logout Mobile */}
        <li className="nav-item">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="nav-link logout-btn"
              title="Sign out"
            >
              Logout
            </button>
          ) : (
            <Link
              to={ROUTES.LOGIN}
              className="nav-link login-btn"
              onClick={closeAllDropdowns}
            >
              Login
            </Link>
          )}
        </li>
      </ul>
    </nav>
  );
};

export default Navbar;